/* eslint-disable @next/next/no-img-element */
import {Button, Modal, ButtonProps, Row} from 'antd';
import useCommon from '@utils/hooks/common';
import styles from './styles';
import classNames from 'classnames';
import CustomModalSheet from '@components/CustomModalSheet';
import {useEffect, useMemo, useState, useRef} from 'react';
import DepositTab from '@components/deposit-withdraw/DepositTab';
import WithdrawTab from '@components/deposit-withdraw/WithdrawTab';
import HistoryTab from '@components/deposit-withdraw/HistoryTab';
import {useRouter} from 'next/router';
import useRequest from '@utils/hooks/useRequestCustom';
import authApi from '@utils/api/auth.api';
import {AxiosError} from 'axios';
import CustomPopup from '@components/CustomPopup';
import PopupContainer from '../PopupContainer';
import MerchantContent from '@components/deposit-withdraw/MerchantContent';
import {isMerchantGateway} from '@utils/gateway/merchant-gateway';

interface CustomModalSheetProps {
  isModalSheetOpen: boolean;
  onModalSheetClose: () => void;
}

const DepositWithdrawModalSheet = (props: CustomModalSheetProps) => {
  const {classes, t, user} = useCommon({
    withTranslation: ['deposit_withdraw'],
    withStyles: styles,
    observers: ['user'],
  });

  const {isModalSheetOpen, onModalSheetClose} = props;

  const router = useRouter();

  const [selectedTab, setSelectedTab] = useState<number>(1);
  const [withdrawTabClickCount, setWithdrawTabClickCount] = useState(0);
  const withdrawInputRef = useRef<HTMLInputElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const handleTabClick = (tabKey: number) => {
    // CRITICAL: For withdraw tab, focus hidden input IMMEDIATELY in same event loop
    if (tabKey === 2 && hiddenInputRef.current) {
      // Step 1: Focus hidden input synchronously (maintains user gesture context)
      hiddenInputRef.current.focus();
      hiddenInputRef.current.click();
      
      // Step 2: Set tab (triggers React re-render)
      setSelectedTab(tabKey);
      setWithdrawTabClickCount(prev => prev + 1);
      
      // Step 3: Transfer focus to real input after it mounts
      // Small delay for React to render the withdraw tab
      setTimeout(() => {
        if (withdrawInputRef.current) {
          // Transfer focus without blurring (keeps keyboard open)
          withdrawInputRef.current.focus();
          withdrawInputRef.current.click();
          
          // Ensure cursor at end
          const len = withdrawInputRef.current.value.length;
          withdrawInputRef.current.setSelectionRange(len, len);
        }
      }, 50);
      
      // Additional retry for stubborn browsers
      setTimeout(() => {
        if (withdrawInputRef.current && document.activeElement !== withdrawInputRef.current) {
          withdrawInputRef.current.focus();
          withdrawInputRef.current.click();
        }
      }, 150);
    } else {
      // Normal tab switch for other tabs
      setSelectedTab(tabKey);
    }
  };

  const tabs = [
    {
      title: t('deposit'),
      key: 1,
      content: <DepositTab />,
    },
    {
      title: t('withdraw'),
      key: 2,
      content: <WithdrawTab key={withdrawTabClickCount} isActive={selectedTab === 2} inputRef={withdrawInputRef} />,
    },
  ];

  return (
    <PopupContainer
      visible={isModalSheetOpen}
      onCancel={onModalSheetClose}
      mobileModalProps={{disableScrollLocking: true}}
      desktopModalProps={isMerchantGateway() ? {maskClosable: false} : undefined}
    >
      {/* Hidden input bridge for mobile keyboard activation */}
      <input
        ref={hiddenInputRef}
        type="tel"
        inputMode="decimal"
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none',
        }}
        onChange={() => {}} // Prevent React warning
      />
      
      {user?.isThirdPartyGateway() && isMerchantGateway() ? (
        <div className={classes.tabsContainer}>
          <div className={classes.tabTitlesRow}>
            {[
              {title: t('deposit'), key: 1},
              {title: t('withdraw'), key: 2},
            ].map((tab, index) => (
              <div
                key={index}
                className={classNames(classes.tabTitle, {
                  active: selectedTab === tab.key,
                })}
                onClick={() => setSelectedTab(tab.key)}
              >
                <div className={classes.tabContent}>{t(`${tab.title}`)}</div>
              </div>
            ))}
          </div>
          <div className={classes.tabContent}>
            <MerchantContent mode={selectedTab === 1 ? 'deposit' : 'withdraw'} />
          </div>
        </div>
      ) : user?.isThirdPartyGateway() ? (
        'Forbidden'
      ) : (
        <div className={classes.tabsContainer}>
          <div className={classes.tabTitlesRow}>
            {tabs.map((tab, index) => (
              <div
                key={index}
                className={classNames(classes.tabTitle, {
                  active: selectedTab === tab.key,
                })}
                onClick={() => handleTabClick(tab.key)}
              >
                <div className={classes.tabContent}>{t(`${tab.title}`)}</div>
              </div>
            ))}
          </div>
          <div className={classes.tabContent}>
            {tabs.find(tab => tab.key === selectedTab)?.content}
          </div>
        </div>
      )}
    </PopupContainer>
  );
};

export default DepositWithdrawModalSheet;
